import React, { useEffect } from "react";
import {
  Container,
  Padding,
  Layout,
  Text,
  Button,
} from "@kidslivesafe/sc-react";
import {
  useAppDispatch,
  useAppSelector,
  useHandlePageNavigate,
} from "../../hooks/hooks";
import {
  setActivatedFromDashboard,
  selectProtectionProgress,
  selectProtectionStatus,
  selectProtectionFlowDisplayedSent,
  setProtectionFlowDisplayedSent,
} from "../../features/profileProtection/profileProtectionSlice";
import { sendMemberEventAsync } from "../../features/admin/adminSlice";
import { clearPeopleSearch } from "../../features/multisearch/multisearchSlice";
import { clearPeopleTeaserReports } from "../../features/reports/reportsSlice";

const statusTextMap = {
  not_started: "Not started",
  in_progress: "In progress",
  completed: "Completed",
};

const ProfileProtectionDash: React.FC = () => {
  const dispatch = useAppDispatch();
  const pageNavigate = useHandlePageNavigate();
  const status = useAppSelector(selectProtectionStatus);
  const progress = useAppSelector(selectProtectionProgress);
  const isStarted = status !== "not_started";
  const hasSentDisplayedEvent = useAppSelector(
    selectProtectionFlowDisplayedSent,
  );

  // Once-per-session impression: gate via redux so we don't fire on every
  // dashboard mount within the SPA. The gate flag is intentionally not
  // persisted (resets on page reload / fresh redux instance) and resets on
  // logout.
  useEffect(() => {
    if (!isStarted && !hasSentDisplayedEvent) {
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Dashboard",
          EventAction: "Protection Flow Displayed",
        }),
      );
      dispatch(setProtectionFlowDisplayedSent(true));
    }
  }, [isStarted, hasSentDisplayedEvent, dispatch]);

  const handleOpenProtection = () => {
    if (status !== "not_started") {
      pageNavigate("/protection/status");
      return;
    }

    dispatch(setActivatedFromDashboard(true));
    dispatch(clearPeopleSearch());
    dispatch(clearPeopleTeaserReports());
    pageNavigate("/protection/search");
  };

  // const handleHowItWorks = () => {
  //   dispatch(clearPeopleSearch());
  //   dispatch(clearPeopleTeaserReports());
  //   pageNavigate("/protection/search");
  // };

  return (
    <Container
      containerTitle="Protect your profile"
      containerId="dashboard__profile-protection__container"
      tooltipContent="Find and remove your profile from people-search sites to protect your privacy."
    >
      <Padding space="md">
        {!isStarted ? (
          <Layout type="flex" flexProps={{ direction: "column", gap: "sm" }}>
            <Text size="sm">
              Your info may be on people-search sites. Find it and start
              removing it in minutes.
            </Text>
            <Layout type="flex" flexProps={{ direction: "row", gap: "md" }}>
              <Text size="xs" color="primary">
                ~2 min to start
              </Text>
              <Text size="xs" color="primary">
                Free with your plan
              </Text>
            </Layout>
            <Layout
              type="flex"
              flexProps={{ justifyContent: "flex-start", gap: "lg" }}
            >
              <Button
                buttonText="Activate Now"
                color="primary"
                fill="fit-content"
                handleClick={handleOpenProtection}
              />
              {/* <AnchorLink role="button" handleClick={handleHowItWorks}>
                How it works
              </AnchorLink> */}
            </Layout>
          </Layout>
        ) : (
          <Layout type="flex" flexProps={{ direction: "column", gap: "sm" }}>
            <Text size="sm" fontWeight="bold">
              Protection status: {statusTextMap[status]}
            </Text>
            <Text size="sm">
              Your removals are active. Continue in Status Hub to track progress
              and monitoring.
            </Text>
            <Text size="sm" fontWeight="semibold">
              {progress.removed} of {progress.total} sites removed (
              {progress.percent}%)
            </Text>
            <Layout type="flex" flexProps={{ direction: "column", gap: "xxs" }}>
              <Text size="xs" color="subtle">
                Free with your plan
              </Text>
            </Layout>
            <Layout type="flex" flexProps={{ justifyContent: "flex-start" }}>
              <Button
                buttonText="View Status"
                color="primary"
                fill="fit-content"
                handleClick={handleOpenProtection}
              />
            </Layout>
          </Layout>
        )}
      </Padding>
    </Container>
  );
};

export default ProfileProtectionDash;
